import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import Layout from './components/Layout/Layout';
import DividendPanel from './components/DividendPanel/DividendPanel';
import GameCategories from './components/GameCategories/GameCategories';
import CryptoFlip from './components/games/sologames/CryptoFlip';
import SpinWheel from './components/games/sologames/SpinWheel';
import CryptoSlots from './components/games/sologames/CryptoSlots';
import LuckyDice from './components/games/sologames/LuckyDice';
import CrystalBallFortune from './components/games/sologames/CrystalBallFortune';
import HiLoCards from './components/games/sologames/HiLoCards';
import BullsEye from './components/games/sologames/BullsEye';
import NumberBlast from './components/games/sologames/NumberBlast';
import SpaceMission from './components/games/sologames/SpaceMission';
import CryptoBlackjack from './components/games/sologames/CryptoBlackjack';
import WalletConnector from './components/WalletConnector';
import { useContract } from './hooks/useContract';
import { formatTokenAmount } from './utils/formatters';
import './styles/App.css';
import './styles/globals.css';

function App() {
  const [currentView, setCurrentView] = useState('home');
  const [walletData, setWalletData] = useState({
    account: null,
    provider: null,
    walletType: null
  });

  const { blbTokenContract } = useContract(walletData);
  const [blbBalance, setBlbBalance] = useState('0');
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  // Load BLB balance
  useEffect(() => {
    if (walletData.account && blbTokenContract) {
      loadBlbBalance();
      const interval = setInterval(loadBlbBalance, 30000);
      return () => clearInterval(interval);
    } else {
      setBlbBalance('0');
    }
  }, [walletData.account, blbTokenContract]);

  const loadBlbBalance = async () => {
    try {
      setIsLoadingBalance(true);
      if (!blbTokenContract || !walletData.account) return;

      const balance = await blbTokenContract.balanceOf(walletData.account);
      setBlbBalance(ethers.utils.formatEther(balance));
    } catch (error) {
      console.log('BLB token not deployed or error loading balance');
      setBlbBalance('0');
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const handleWalletConnect = (account, provider, walletType) => {
    setWalletData({ account, provider, walletType });
    checkAndSwitchNetwork(provider);
  };

  const handleWalletDisconnect = () => {
    setWalletData({ account: null, provider: null, walletType: null });
  };

  const handleNavigate = (view) => {
    setCurrentView(view);
  };

  const handleBackToHome = () => {
    setCurrentView('home');
  };

  const checkAndSwitchNetwork = async (provider) => {
    try {
      const network = await provider.getNetwork();
      if (network.chainId !== 56 && network.chainId !== 56) {
        await switchToSupportedNetwork();
      }
    } catch (error) {
      console.error('Error checking network:', error);
    }
  };

  const switchToSupportedNetwork = async () => {
    if (!window.ethereum) return;

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x38' }], // BSC mainnet
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: '0x38',
                chainName: 'BNB Smart Chain',
                nativeCurrency: {
                  name: 'BNB',
                  symbol: 'BNB',
                  decimals: 18
                },
                rpcUrls: ['https://bsc-dataseed1.binance.org/'],
                blockExplorerUrls: ['https://bscscan.com']
              },
            ],
          });
        } catch (addError) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: '0x15eb', // opBNB testnet (5611)
                  chainName: 'opBNB Testnet',
                  nativeCurrency: {
                    name: 'tBNB',
                    symbol: 'tBNB',
                    decimals: 18
                  },
                  rpcUrls: ['https://opbnb-testnet-rpc.bnbchain.org/'],
                  blockExplorerUrls: ['https://testnet.opbnbscan.com']
                },
              ],
            });
          } catch (opbnbError) {
            console.error('Error adding opBNB testnet', opbnbError);
          }
        }
      }
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'cryptoflip':
        return (
          <CryptoFlip 
            walletData={walletData}
            onBack={handleBackToHome}
          />
        );
      case 'SpinWheel':
        return (
          <SpinWheel 
            walletData={walletData}
            onBack={handleBackToHome}
          />
        );
      case 'CryptoSlots':
        return (
          <CryptoSlots 
            walletData={walletData}
            onBack={handleBackToHome}
          />
        );
      case 'LuckyDice':
        return (
          <LuckyDice 
            walletData={walletData}
            onBack={handleBackToHome}
          />
        );
        case 'CrystalBallFortune':
        return (
          <CrystalBallFortune 
            walletData={walletData}
            onBack={handleBackToHome}
          />
        );
        case 'NumberBlast':
        return (
          <NumberBlast 
            walletData={walletData}
            onBack={handleBackToHome}
          />
        );
        case 'HiLoCards':
        return (
          <HiLoCards 
            walletData={walletData}
            onBack={handleBackToHome}
          />
        );
        case 'BullsEye':
        return (
          <BullsEye 
            walletData={walletData}
            onBack={handleBackToHome}
          />
        );
        case 'SpaceMission':
        return (
          <SpaceMission 
            walletData={walletData}
            onBack={handleBackToHome}
          />
        );
        case 'CryptoBlackjack':
        return (
          <CryptoBlackjack 
            walletData={walletData}
            onBack={handleBackToHome}
          />
        );
      case 'solo':
      case 'multiplayer':
      case 'arcade':
        return (
          <GameCategories 
            category={currentView}
            walletData={walletData}
            onNavigate={handleNavigate}
            onBack={handleBackToHome}
          />
        );
      default:
        return (
          <div className="battle-command-center">
            {/* ⚡ DIRECT WARRIOR AUTHENTICATION ⚡ */}
            <div className="warrior-authentication">
              <WalletConnector
                onConnect={handleWalletConnect}
                onDisconnect={handleWalletDisconnect}
                onError={(error) => console.error('⚠️ Battle Station Error:', error)}
                onChainChanged={(chainId) => console.log('⚡ Network Changed:', chainId)}
                requiredChainId={56}
                buttonClassName="battle-connect-btn"
              />
              
              {/* 💎 WARRIOR RESOURCES DISPLAY */}
              {walletData.account && (
                <div className="warrior-resources">
                  <div className="resource-badge">
                    <span className="resource-icon">💎</span>
                    <span className="resource-label">Battle Tokens:</span>
                    <span className="resource-amount">
                      {isLoadingBalance ? (
                        <span className="loading-battle">⚡ Loading...</span>
                      ) : (
                        `${formatTokenAmount(blbBalance)} BLB`
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <DividendPanel walletData={walletData} />
            <GameCategories 
              category="all"
              walletData={walletData}
              onNavigate={handleNavigate}
            />
          </div>
        );
    }
  };

  return (
    <div className="App">
      <Layout 
        walletData={walletData}
        onWalletConnect={handleWalletConnect}
        onWalletDisconnect={handleWalletDisconnect}
        onNavigate={handleNavigate}
        currentView={currentView}
      >
        {renderCurrentView()}
      </Layout>
    </div>
  );
}

export default App;